import { Fragment, type ReactNode, useEffect, useId } from "react";
import { useChat } from "../../providers/chat-provider";

export type FooterFormProps = {
  children: ReactNode;
};

/** Shared across FooterForm instances so a replaced owner does not clear the next one. */
let footerFormOwner: object | null = null;

/**
 * Portals `children` into the chat footer.
 *
 * Cleanup is deferred to a microtask so that when one FooterForm is replaced by
 * another in the same commit, the new owner can claim the footer before the old
 * instance clears it.
 *
 * Children are captured on mount only — put a `key` on FooterForm to remount when
 * the step (form → success) changes, instead of syncing every parent render.
 *
 * The registered node carries this instance's own key. The footer animates its slot and
 * needs to tell one form from the next: the children themselves are unkeyed, so without
 * this a step advancing to the following form would land on the same presence and swap
 * its content with no transition at all.
 */
export const FooterForm = ({ children }: FooterFormProps) => {
  const { setFooterContent } = useChat();
  const contentKey = useId();

  useEffect(() => {
    const owner = {};
    footerFormOwner = owner;
    setFooterContent(<Fragment key={contentKey}>{children}</Fragment>);

    return () => {
      queueMicrotask(() => {
        if (footerFormOwner === owner) {
          footerFormOwner = null;
          setFooterContent(null);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount via `key` when step changes
  }, [setFooterContent, contentKey]);

  return null;
};
